/**
 * ReportCard — card for a single report in the reports listing.
 *
 * Extracted from the 4 Reports components where it was duplicated.
 * Section-specific badges are passed via `renderBadges` render prop.
 */
import { useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  MoreHorizontal,
  Clock,
  ExternalLink,
  Copy,
  Pencil,
  Trash2,
  Link,
} from 'lucide-react';
import { cn } from '@/lib/cn';
import { Card } from '@/components/ui/Card';
import { DropdownMenu, ConfirmDialog, PromptDialog } from '@/components/ui/DropdownMenu';
import type { MenuEntry } from '@/components/ui/DropdownMenu';
import { staggerItem } from '@/design/animations';
import { api } from '@/services/api';
import { endpoints } from '@/services/endpoints';
import { formatRelativeTime } from '@/lib/format';
import { toast } from '@/components/ui/Toast';
import type { Report } from '@/lib/reports.types';

interface ReportCardProps {
  report: Report;
  sectionId: string;
  /** Route prefix, e.g., '/product' — report links to `{sectionRoute}/reports/{id}` */
  sectionRoute: string;
  onRefresh: () => void;
  /** Icon to show in the card header (optional) */
  icon?: React.ReactNode;
  /** Background color class for the icon container */
  iconBgClass?: string;
  /** Render section-specific config badges */
  renderBadges?: (config: Record<string, unknown>) => React.ReactNode;
}

export function ReportCard({
  report,
  sectionId,
  sectionRoute,
  onRefresh,
  icon,
  iconBgClass,
  renderBadges,
}: ReportCardProps) {
  const navigate = useNavigate();
  const menuBtnRef = useRef<HTMLButtonElement>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [showRename, setShowRename] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  const reportPath = `${sectionRoute}/reports/${report.id}`;

  const handleOpen = useCallback(() => navigate(reportPath), [navigate, reportPath]);

  const handleDuplicate = useCallback(async () => {
    setActionLoading(true);
    try {
      await api.post(endpoints.reports.duplicate(report.id, sectionId));
      onRefresh();
      toast.success('Report duplicated');
    } catch {
      toast.error('Failed to duplicate report');
    }
    setActionLoading(false);
  }, [report.id, sectionId, onRefresh]);

  const handleCopyLink = useCallback(() => {
    navigator.clipboard
      .writeText(`${window.location.origin}${reportPath}`)
      .then(() => toast.success('Link copied'))
      .catch(() => toast.error('Failed to copy link'));
  }, [reportPath]);

  const handleRename = useCallback(
    async (newName: string) => {
      setActionLoading(true);
      try {
        await api.patch(endpoints.reports.update(report.id, sectionId), { name: newName });
        onRefresh();
        toast.success('Report renamed');
      } catch {
        toast.error('Failed to rename report');
      }
      setActionLoading(false);
      setShowRename(false);
    },
    [report.id, sectionId, onRefresh],
  );

  const handleDelete = useCallback(async () => {
    setActionLoading(true);
    try {
      await api.delete(endpoints.reports.delete(report.id, sectionId));
      onRefresh();
      toast.success('Report deleted');
    } catch {
      toast.error('Failed to delete report');
    }
    setActionLoading(false);
    setShowDelete(false);
  }, [report.id, sectionId, onRefresh]);

  const menuIconCls = 'w-4 h-4';
  const menuItems: MenuEntry[] = [
    { label: 'Open', icon: <ExternalLink className={menuIconCls} />, onClick: handleOpen },
    { label: 'Duplicate', icon: <Copy className={menuIconCls} />, onClick: handleDuplicate },
    { label: 'Copy link', icon: <Link className={menuIconCls} />, onClick: handleCopyLink },
    { type: 'divider' },
    { label: 'Rename', icon: <Pencil className={menuIconCls} />, onClick: () => setShowRename(true) },
    { type: 'divider' },
    { label: 'Delete', icon: <Trash2 className={menuIconCls} />, onClick: () => setShowDelete(true), variant: 'danger' },
  ];

  return (
    <motion.div variants={staggerItem}>
      <Card interactive padding="none" onClick={handleOpen} className="group">
        <div className="p-4 pb-3">
          <div className="flex items-start justify-between mb-2">
            <div className="flex items-center gap-2.5 min-w-0">
              {icon && (
                <div
                  className={cn(
                    'w-8 h-8 rounded-lg flex items-center justify-center shrink-0',
                    iconBgClass,
                  )}
                >
                  {icon}
                </div>
              )}
              <div className="min-w-0">
                <h4 className="text-sm font-semibold text-slate-900 truncate">{report.name}</h4>
              </div>
            </div>
            <button
              ref={menuBtnRef}
              className={cn(
                'w-7 h-7 rounded-lg flex items-center justify-center shrink-0',
                'text-slate-300 hover:text-slate-500 hover:bg-slate-100',
                menuOpen
                  ? 'opacity-100 bg-slate-100 text-slate-500'
                  : 'opacity-0 group-hover:opacity-100',
                'transition-all duration-150',
              )}
              onClick={(e) => {
                e.stopPropagation();
                setMenuOpen((prev) => !prev);
              }}
            >
              <MoreHorizontal className="w-4 h-4" />
            </button>
          </div>

          {report.description && (
            <p className="text-xs text-slate-500 line-clamp-2 mb-2">{report.description}</p>
          )}

          {renderBadges && (
            <div className="flex flex-wrap gap-1.5 mt-2.5">
              {renderBadges(report.config)}
            </div>
          )}
        </div>

        <div className="px-4 py-2.5 border-t border-slate-100 flex items-center gap-1.5">
          <Clock className="w-3 h-3 text-slate-400" />
          <span className="text-2xs text-slate-400">{formatRelativeTime(report.updatedAt)}</span>
        </div>
      </Card>

      <DropdownMenu
        items={menuItems}
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
        anchorRef={menuBtnRef}
        align="right"
      />
      <PromptDialog
        open={showRename}
        onClose={() => setShowRename(false)}
        onSubmit={handleRename}
        title="Rename Report"
        label="Report name"
        defaultValue={report.name}
        placeholder="Enter new name"
        submitLabel="Rename"
        loading={actionLoading}
      />
      <ConfirmDialog
        open={showDelete}
        onClose={() => setShowDelete(false)}
        onConfirm={handleDelete}
        title="Delete Report"
        description={`Are you sure you want to delete "${report.name}"? This action cannot be undone.`}
        confirmLabel="Delete"
        variant="danger"
        loading={actionLoading}
      />
    </motion.div>
  );
}
